import React from "react";
import { Button } from "@/components/ui/button";
import { Plane, Building2, FileCheck, ArrowRight } from "lucide-react";

const services = [
  {
    icon: Plane,
    title: "Flight Bookings",
    subtitle: "First Class & Private Jets",
    description:
      "Access to premium cabins, private aviation, and exclusive airport services. We secure the best routes and ensure seamless travel from takeoff to landing.",
    features: [
      "First & Business Class bookings",
      "Private jet charters",
      "VIP airport lounges",
      "Seamless connections",
    ],
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80",
  },
  {
    icon: Building2,
    title: "Hotel Reservations",
    subtitle: "World's Finest Properties",
    description:
      "Exclusive partnerships with the world's most prestigious hotels and resorts. Enjoy complimentary upgrades, special amenities, and personalized experiences.",
    features: [
      "Luxury resort bookings",
      "Complimentary upgrades",
      "Special VIP amenities",
      "Private villa rentals",
    ],
    image: "https://images.unsplash.com/photo-1601999705946-fbf42c3c6c66?w=800&q=80",
  },
  {
    icon: FileCheck,
    title: "Visa Assistance",
    subtitle: "Hassle-Free Documentation",
    description:
      "Complete visa support for any destination worldwide. Our specialists handle all documentation, appointments, and follow-ups for a stress-free experience.",
    features: [
      "Complete documentation support",
      "Express processing available",
      "Embassy appointment scheduling",
      "Application tracking",
    ],
    image: "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?w=800&q=80",
  },
];

export const ServicesSection = () => {
  return (
    <section
      id="services"
      className="py-20 lg:py-32 bg-muted/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-primary text-sm font-semibold tracking-wider uppercase mb-3">
            What We Offer
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our <span className="text-gradient-gold">Premium Services</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mb-6" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Comprehensive luxury travel services tailored to your every need
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-12 lg:space-y-20">
          {services.map((service, index) => (
            <div
              key={service.title}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                index % 2 === 1 ? "lg:grid-flow-col-dense" : ""
              }`}
            >
              {/* Image */}
              <div
                className={`relative group ${
                  index % 2 === 1 ? "lg:col-start-2" : ""
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-elegant">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  {/* Icon Badge */}
                  <div className="absolute bottom-6 left-6 p-4 rounded-xl bg-background/95 backdrop-blur-sm border border-secondary/30 shadow-gold">
                    <service.icon className="w-8 h-8 text-secondary" />
                  </div>
                </div>

                {/* Decorative Border */}
                <div
                  className={`absolute -z-10 rounded-2xl border border-secondary/30 ${
                    index % 2 === 0
                      ? "-bottom-4 -right-4 top-4 left-4"
                      : "-bottom-4 -left-4 top-4 right-4"
                  }`}
                />
              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? "lg:col-start-1" : ""}>
                <span className="inline-block text-primary text-sm font-medium tracking-wider uppercase mb-2">
                  {service.subtitle}
                </span>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  {service.title}
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-secondary" />
                      <span className="text-foreground/80">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button variant="luxuryOutline" size="lg" className="group">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
